/**
 * Custom Hook: useFunnelState
 * 
 * Provides convenient access to funnel state and common operations.
 * Wraps the FunnelContext with additional utility functions.
 */

import { useMemo } from 'react';
import { useFunnel } from '../context/FunnelContext';
import { getQuestionFlow } from '../data/questions';

/**
 * Hook that provides funnel state with computed properties
 * 
 * @returns Object containing state and utility functions
 */
export function useFunnelState() {
  const funnel = useFunnel();
  
  /**
   * Get the full question flow based on current service type
   * Memoized to avoid recalculating on every render
   */
  const questionFlow = useMemo(() => {
    return getQuestionFlow(funnel.state.serviceType);
  }, [funnel.state.serviceType]);

  /**
   * Get the current question based on step number
   */
  const currentQuestion = useMemo(() => {
    // Step is 1-indexed, array is 0-indexed
    return questionFlow[funnel.state.currentStep - 1];
  }, [questionFlow, funnel.state.currentStep]);

  /**
   * Check if we're on the first step
   */
  const isFirstStep = funnel.state.currentStep === 1;

  /**
   * Check if we're on the last question step
   * (before thank you page)
   */
  const isLastQuestionStep = funnel.state.currentStep === questionFlow.length;

  /**
   * Check if we're on the thank you page
   */
  const isThankYouStep = funnel.state.currentStep > questionFlow.length;

  /**
   * Calculate accurate progress based on current flow
   */
  const accurateProgress = useMemo(() => {
    const totalSteps = questionFlow.length + 1; // +1 for thank you
    return Math.round(((funnel.state.currentStep - 1) / (totalSteps - 1)) * 100);
  }, [questionFlow.length, funnel.state.currentStep]);

  /**
   * Get answer for current question
   */
  const currentAnswer = useMemo(() => {
    if (!currentQuestion) return undefined;
    return funnel.state.answers[currentQuestion.id];
  }, [currentQuestion, funnel.state.answers]);

  /**
   * Check if current step has a valid answer
   */
  const hasValidAnswer = useMemo(() => {
    if (!currentQuestion) return false;
    const answer = currentAnswer;
    
    // Check if answer exists and is not empty
    if (answer === undefined || answer === null) return false;
    if (typeof answer === 'string' && answer.trim() === '') return false;
    
    return true;
  }, [currentQuestion, currentAnswer]);

  /**
   * Get all answers as a formatted object
   */
  const formattedAnswers = useMemo(() => {
    return Object.entries(funnel.state.answers).map(([key, value]) => ({
      questionId: key,
      answer: value,
    }));
  }, [funnel.state.answers]);

  /**
   * Check if funnel can proceed to next step
   * (has valid answer for required questions)
   */
  const canProceed = useMemo(() => {
    if (!currentQuestion) return true;
    
    // If question has validation and is required, check for answer
    if (currentQuestion.validation?.required) {
      return hasValidAnswer;
    }
    
    // For selection questions, always require an answer
    if (currentQuestion.type === 'single-select' || currentQuestion.type === 'multi-select') {
      return hasValidAnswer;
    }
    
    return true;
  }, [currentQuestion, hasValidAnswer]);

  return {
    // Base funnel context
    ...funnel,
    
    // Computed properties
    questionFlow,
    currentQuestion,
    currentAnswer,
    isFirstStep,
    isLastQuestionStep,
    isThankYouStep,
    accurateProgress,
    hasValidAnswer,
    canProceed,
    formattedAnswers,
    
    // Counts
    totalQuestions: questionFlow.length,
    totalSteps: questionFlow.length + 1, // Including thank you
  };
}

export default useFunnelState;
